import {
  FeatherActivity,
  FeatherAlertTriangle,
  FeatherCalendar,
  FeatherCheckCircle,
  FeatherClock,
  FeatherCreditCard,
  FeatherFileText,
  FeatherMail,
  FeatherRefreshCw,
  FeatherRotateCcw,
  FeatherSend,
  FeatherUserCheck,
  FeatherXCircle,
} from '@subframe/core';
import type { ComponentType } from 'react';

import type {
  FaturaHistoricoAcao,
  FaturaStatus,
  FaturaTipoGeracao,
  MetodoPagamento,
} from '../types';

type BadgeVariant = 'success' | 'warning' | 'error' | 'neutral' | 'brand';

interface StatusConfig {
  label: string;
  variant: BadgeVariant;
  icon: ComponentType<{ className?: string }>;
}

export const faturaStatusConfig: Record<FaturaStatus, StatusConfig> = {
  EMITIDA: { label: 'Emitida', variant: 'brand', icon: FeatherSend },
  PAGA: { label: 'Paga', variant: 'success', icon: FeatherCheckCircle },
  VENCIDA: { label: 'Vencida', variant: 'error', icon: FeatherAlertTriangle },
  CANCELADA: { label: 'Cancelada', variant: 'neutral', icon: FeatherXCircle },
  ESTORNADA: { label: 'Estornada', variant: 'warning', icon: FeatherRotateCcw },
  ERRO_EMISSAO: { label: 'Erro de emissão', variant: 'error', icon: FeatherAlertTriangle },
};

export const tipoGeracaoLabel: Record<FaturaTipoGeracao, string> = {
  MANUAL: 'Manual',
  EM_LOTE: 'Em lote',
  AGENDADA: 'Agendada',
};

export const tipoGeracaoIcon: Record<FaturaTipoGeracao, ComponentType<{ className?: string }>> = {
  MANUAL: FeatherUserCheck,
  EM_LOTE: FeatherActivity,
  AGENDADA: FeatherCalendar,
};

export const metodoPagamentoLabel: Record<MetodoPagamento, string> = {
  PIX: 'PIX',
  BOLETO: 'Boleto',
  CARTAO: 'Cartão',
};

export const metodoPagamentoIcon: Record<MetodoPagamento, ComponentType<{ className?: string }>> = {
  PIX: FeatherRefreshCw,
  BOLETO: FeatherFileText,
  CARTAO: FeatherCreditCard,
};

export const historicoAcaoConfig: Record<FaturaHistoricoAcao, StatusConfig> = {
  CRIADA: { label: 'Fatura criada', variant: 'neutral', icon: FeatherFileText },
  EMITIDA_ASAAS: { label: 'Emitida no Asaas', variant: 'brand', icon: FeatherSend },
  PAGAMENTO_CONFIRMADO: {
    label: 'Pagamento confirmado',
    variant: 'success',
    icon: FeatherCheckCircle,
  },
  PAGAMENTO_MANUAL: { label: 'Pagamento manual', variant: 'success', icon: FeatherCheckCircle },
  SEGUNDA_VIA_ENVIADA: { label: '2ª via enviada', variant: 'brand', icon: FeatherMail },
  VENCIDA_AUTOMATICA: { label: 'Marcada vencida', variant: 'error', icon: FeatherAlertTriangle },
  CANCELADA: { label: 'Cancelada', variant: 'neutral', icon: FeatherXCircle },
  CANCELADA_POR_REABERTURA: {
    label: 'Cancelada por reabertura',
    variant: 'warning',
    icon: FeatherRotateCcw,
  },
  REEMITIDA: { label: 'Reemitida', variant: 'brand', icon: FeatherRefreshCw },
  ESTORNADA: { label: 'Estornada', variant: 'warning', icon: FeatherRotateCcw },
  ERRO_EMISSAO_ASAAS: { label: 'Erro de emissão', variant: 'error', icon: FeatherAlertTriangle },
  COMPROVANTE_ANEXADO: { label: 'Comprovante anexado', variant: 'success', icon: FeatherFileText },
};

export const VencimentoClockIcon = FeatherClock;

const ASAAS_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Aguardando pagamento',
  RECEIVED: 'Recebida',
  CONFIRMED: 'Confirmada',
  OVERDUE: 'Vencida',
  REFUNDED: 'Estornada',
  RECEIVED_IN_CASH: 'Recebida em dinheiro',
  REFUND_REQUESTED: 'Estorno solicitado',
  REFUND_IN_PROGRESS: 'Estorno em andamento',
  CHARGEBACK_REQUESTED: 'Chargeback solicitado',
  CHARGEBACK_DISPUTE: 'Chargeback em disputa',
  AWAITING_CHARGEBACK_REVERSAL: 'Aguardando reversão de chargeback',
  DUNNING_REQUESTED: 'Cobrança ativa solicitada',
  DUNNING_RECEIVED: 'Cobrança ativa recebida',
  AWAITING_RISK_ANALYSIS: 'Em análise de risco',
  CANCELED: 'Cancelada',
  CANCELLED: 'Cancelada',
  CANCELED_BY_MANUAL_PAYMENT: 'Cancelada (pagamento manual)',
  CANCELED_BY_REOPEN: 'Cancelada (reabertura)',
  DELETED: 'Removida no Asaas',
};

/**
 * Status do gateway que não admitem mais pagamento — já recebidos, estornados ou
 * cancelados. Serve para não oferecer o link do boleto quando a cobrança acabou
 * no Asaas mas a fatura local ainda não reflete isso: cancelamento aceito lá cuja
 * escrita local falhou, ou webhook de pagamento em atraso, em que reenviar o link
 * pede um segundo pagamento de um boleto já quitado.
 *
 * `DELETED` fica de fora porque nunca chega do Asaas — remover não é transição de
 * status; ele existe aqui só como rótulo de dado legado.
 */
const ASAAS_STATUS_ENCERRADOS = new Set([
  'RECEIVED',
  'CONFIRMED',
  'RECEIVED_IN_CASH',
  'REFUNDED',
  'CANCELED',
  'CANCELLED',
  'CANCELED_BY_MANUAL_PAYMENT',
  'CANCELED_BY_REOPEN',
]);

export function statusAsaasEncerrado(status: string | null | undefined): boolean {
  return status != null && ASAAS_STATUS_ENCERRADOS.has(status);
}

export function formatarStatusAsaas(status: string | null | undefined): string {
  if (!status) return '—';
  return ASAAS_STATUS_LABEL[status] ?? status;
}
